// ─────────────────────────────────────────────────────────────
// LukaDuka - AuthScreen (ver4)
// Login / create-account screen.
//
// Changes from ver3:
//   Password minimum (3 Nov 2026): NIST - 15 characters, no required mix.
//   - Create Account: placeholder "Password", shared hint line + live
//     "N more characters to go" (PasswordHint) replace the checklist and
//     the match tick. The button stays tappable; a tap shows the
//     too-short or "Passwords don't match." message.
//   - Signup errors: never raw Supabase text. Password rejections via
//     passwordErrorMessage; the email rate limit gets its own stopgap
//     message (full onboarding session to revisit); anything else is
//     "Something went wrong. Please try again."
//   - Empty check is now "is anything typed" (no trim) - a password made
//     of spaces is a real password.
//   - Sign In unchanged: no password rules, older passwords still work.
//
// Changes from ver2:
//   - ADD: "Forgot password?" on the Sign In tab (Piece 1 of the reset
//     flow). It calls supabase.auth.resetPasswordForEmail with a redirect
//     to the reset page (window.location.origin + '/?reset=true'), using
//     the email already typed above. Shows a NEUTRAL message every time -
//     it never reveals whether an address has an account (no enumeration).
//   - EMAIL PATH: this is Option A (Supabase's built-in mailer) - the
//     documented interim while RESEND_API_KEY / send-email (Option B) is
//     not yet live. To migrate to B, swap the resetPasswordForEmail call
//     for a call to the recovery Edge Function. Nothing else changes.
//   - CONFIG PREREQUISITE (not code): the redirect target .../?reset=true
//     must be on Supabase's allowed Redirect URLs (add the origin with a
//     /** wildcard), separately for staging and production, or Supabase
//     refuses the redirect and no link is sent.
//
// Changes from ver1:
//   - Signup uses the shared password policy (src/shared/password/
//     passwordRules). Stale "min 6 characters" removed. Live checklist +
//     match tick. Create Account disabled until valid. Sign In unaffected.
//     Signup errors mapped via friendlyPasswordError.
//
// Colour-token note: this screen still uses a hardcoded palette (P). It
// is part of the known colour-token debt - migrate with ResetPassword and
// passwordRules together in the token sweep.
// ─────────────────────────────────────────────────────────────

import { useState } from 'react'
import { supabase } from '@/shared/services/supabase'
import { LukaLoadingScreen } from '@/shared/components/LukaLoadingScreen'
import { passwordTooShort, passwordErrorMessage, PasswordHint } from '@/shared/password/passwordRules'

const P = {
  bg: '#EDE4D8', card: '#FFFFFF', text: '#1C1917',
  textMid: '#78716C', textLight: '#A8A29E', border: '#E7E5E4',
  accent: '#5B7A5E', accentLight: '#EDF2ED', danger: '#C0392B',
}
const sans = "'DM Sans', 'Helvetica Neue', sans-serif"

// Password minimum (3 Nov 2026): stopgap for Supabase's email rate limit
// ("you can only request this after N seconds" / over_email_send_rate_limit
// / HTTP 429). The full onboarding session will revisit this wording.
const RATE_LIMIT_MESSAGE = "We've just had a request for this email. Please wait a minute, then try again."
function isRateLimited(error: { status?: number; code?: string; message?: string }): boolean {
  if (error.status === 429) return true
  if (error.code === 'over_email_send_rate_limit' || error.code === 'over_request_rate_limit') return true
  return /only request this after|rate limit|too many requests/.test((error.message || '').toLowerCase())
}

export default function AuthScreen() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  async function handleSubmit() {
    setError('')
    setInfo('')
    // Password minimum (3 Nov 2026): "is anything typed" - never trim the
    // password, a phrase can contain (or be) spaces.
    if (!email.trim() || !password) { setError('Please enter your email and password.'); return }
    if (mode === 'signup') {
      // Password minimum (3 Nov 2026): the button stays tappable; the
      // checks run here so the person is told why, not just blocked.
      const tooShort = passwordTooShort(password)
      if (tooShort) { setError(tooShort); return }
      if (password !== confirm) { setError("Passwords don't match."); return }
    }
    setLoading(true)
    if (mode === 'signup') {
      const { data, error } = await supabase.auth.signUp({ email: email.trim(), password })
      if (error) {
        // Password minimum (3 Nov 2026): never show raw Supabase text.
        setError(isRateLimited(error)
          ? RATE_LIMIT_MESSAGE
          : passwordErrorMessage(error) ?? 'Something went wrong. Please try again.')
        setLoading(false)
        return
      }
      if (!data.session) {
        // Email confirmation is required - there's no session yet, so no
        // handoff to the app is coming. Drop back to the form with a
        // friendly notice rather than hang on the loading screen.
        setLoading(false)
        setMode('signin')
        setInfo('Almost there - check your email to confirm your account, then sign in.')
        return
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
      if (error) { setError('Incorrect email or password.'); setLoading(false); return }
    }
    // Genuine success with a session. Show the branded loading screen
    // until the Router picks up SIGNED_IN and swaps us for the app.
    // (We only reach here on success, so the warm screen never shows
    // for a failed attempt.)
    setSuccess(true)
   }

  // Piece 1 of the password-reset flow. Sends a recovery link to the email
  // typed above and always shows the SAME neutral message, so we never
  // reveal whether an address is registered. The redirect is built from
  // the current origin, so it points at staging on staging and production
  // on production automatically.
  async function handleForgotPassword() {
    setError('')
    setInfo('')
    if (!email.trim()) { setError('Enter your email address above, then tap "Forgot password?".'); return }
    setLoading(true)
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + '/?reset=true',
      })
    } catch {
      // Deliberately swallowed - we show the same neutral message whether
      // the call succeeded, the address does not exist, or it errored, so
      // nothing about account existence leaks.
    }
    setLoading(false)
    setInfo('If that address has an account, a reset link is on its way. Please check your email.')
  }

  // Authenticated - hand over to the shared "getting things ready" screen.
  if (success) {
    return <LukaLoadingScreen ready={false} />
  }

  return (
    <div style={{ minHeight: '100vh', background: P.bg, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '40px 24px', fontFamily: sans }}>

      {/* Logo */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 32 }}>
        <div style={{ width: 72, height: 72, background: P.accent, borderRadius: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
          <svg width="46" height="46" viewBox="0 0 200 200" fill="none">
            <ellipse cx="82" cy="87" rx="11" ry="11.5" fill="white"/>
            <circle cx="87" cy="82" r="3.5" fill="#5B7A5E"/>
            <ellipse cx="118" cy="89" rx="10" ry="11" fill="white"/>
            <circle cx="123" cy="84" r="3" fill="#5B7A5E"/>
            <path d="M 77,112 C 84,128 116,132 125,114 C 139,97 143,74 135,58 C 127,43 108,38 95,40 C 70,43 43,63 41,92 C 39,120 54,150 82,161 C 108,171 144,161 161,139 C 179,115 179,81 165,57 C 150,31 122,19 97,21 C 64,24 31,45 16,74 C 2,101 4,136 20,161 C 28,174 40,184 53,190"
              stroke="white" strokeWidth="7" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 28,
          fontWeight: 400, letterSpacing: '-0.02em', color: P.text, marginBottom: 5 }}>
          Luka<span style={{ color: P.accent }}>Duka</span>
        </div>
        <div style={{ fontFamily: "'Lora', Georgia, serif", fontStyle: 'italic',
          fontSize: 13, color: P.textLight }}>your life, your story</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: P.border, borderRadius: 10,
        padding: 3, marginBottom: 24, width: '100%', maxWidth: 340 }}>
        {(['signin', 'signup'] as const).map(m => (
          <button key={m} onClick={() => { setMode(m); setError(''); setInfo('') }}
            style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none',
              background: mode === m ? P.card : 'transparent',
              color: mode === m ? P.text : P.textMid,
              fontSize: 14, fontWeight: mode === m ? 600 : 500,
              cursor: 'pointer', fontFamily: sans,
              boxShadow: mode === m ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
            {m === 'signin' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {/* Form */}
      <div style={{ width: '100%', maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Email address" autoComplete="email"
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12,
            border: '1.5px solid ' + P.border, fontSize: 15, fontFamily: sans,
            background: P.card, color: P.text, outline: 'none', boxSizing: 'border-box' }} />
        <input type="password" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          placeholder="Password"
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          style={{ width: '100%', padding: '13px 16px', borderRadius: 12,
            border: '1.5px solid ' + P.border, fontSize: 15, fontFamily: sans,
            background: P.card, color: P.text, outline: 'none', boxSizing: 'border-box' }} />
        {mode === 'signin' && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={handleForgotPassword} disabled={loading}
              style={{ background: 'none', border: 'none', padding: '2px 0',
                cursor: loading ? 'not-allowed' : 'pointer', fontSize: 13,
                color: P.accent, fontFamily: sans, fontWeight: 500 }}>
              Forgot password?
            </button>
          </div>
        )}
        {mode === 'signup' && (
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Confirm password" autoComplete="new-password"
            style={{ width: '100%', padding: '13px 16px', borderRadius: 12,
              border: '1.5px solid ' + P.border, fontSize: 15, fontFamily: sans,
              background: P.card, color: P.text, outline: 'none', boxSizing: 'border-box' }} />
        )}
        {mode === 'signup' && <PasswordHint password={password} />}
        {error && (
          <div style={{ fontSize: 13, color: P.danger, padding: '10px 14px',
            background: '#FDF0F0', borderRadius: 10, lineHeight: 1.4 }}>{error}</div>
        )}
        {info && (
          <div style={{ fontSize: 13, color: P.accent, padding: '10px 14px',
            background: P.accentLight, borderRadius: 10, lineHeight: 1.4 }}>{info}</div>
        )}
        <button onClick={handleSubmit} disabled={loading}
          style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none',
            background: P.accent, color: '#fff', fontSize: 15, fontWeight: 600,
            fontFamily: sans, cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1, marginTop: 4 }}>
          {loading ? '…' : mode === 'signin' ? 'Sign In' : 'Create Account'}
        </button>
      </div>

      {/* Demo link */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: P.textLight, marginBottom: 8 }}>or</div>
        <button onClick={function() { window.location.href = window.location.origin + '/?demo=true'; }}
          style={{ padding: '12px 32px', borderRadius: 12, border: 'none',
            background: 'linear-gradient(135deg, #C4704B, #E8724A)', color: '#fff',
            fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: sans }}>
          ✨ See a Demo
        </button>
        <div style={{ fontSize: 12, color: P.textLight, marginTop: 6, fontStyle: 'italic' }}>
          Explore Sarah Mitchell's life record
        </div>
      </div>
    </div>
  )
}
